import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PriceVariantDTO, SupplierPreference, UnitType } from '@api/generated/search-service';
import Counter from '@components/forms/Counter.tsx';
import { useCart } from '@hooks/useCart.ts';
import { formatMoney, formatPriceGuarantee, formatUnit } from '@utils/formats.ts';
import PriceTypeComponent from '@features/pricetypes/PriceTypeComponent.tsx';
import Tag from '@components/ui/Tag.tsx';
import SupplierPreferenceToggle from '@features/product-detail/comp/SupplierPreferenceToggle.tsx';
import { isVariantValidOn } from '@utils/priceVariants.ts';
import SupplierAvailabilityTag from '@features/supplier-availability/SupplierAvailabilityTag.tsx';

export interface Supplier {
  priceVariant: PriceVariantDTO;
  supplierName: string;
  supplierId: number;
  onlyMultiply: boolean;
  minimumQuantity: number;
  auctionUnit: UnitType;
  auctionAmount: number;
  supplierPreference?: SupplierPreference;
  /** Den, kdy tenhle dodavatel doveze (z detailu produktu). Proti němu se posuzuje platnost ceny. */
  deliveryDate?: string;
  /** Dostupnost u dodavatele ze synchronizace e-shopu (null = nezjištěná / zastaralá). */
  availability?: string | null;
}

interface SuppliersListProps {
  suppliers: Supplier[];
  productId: number;
  selectedPriceVariant: PriceVariantDTO;
  onSelectPriceVariant: (priceVariant: PriceVariantDTO) => void;
  onAddToCart: (supplierId: number, priceVariantDTO: PriceVariantDTO, quantity: number) => void;
  onDeleteCartItem: (cartItemId: number) => void;
  onAddSupplier?: () => void;
  onSetSupplierPreference?: (supplierId: number, preference: SupplierPreference | null) => void;
}

const SuppliersList: React.FC<SuppliersListProps> = ({
  suppliers,
  onSelectPriceVariant,
  onAddToCart,
  selectedPriceVariant,
  productId,
  onDeleteCartItem,
  onAddSupplier,
  onSetSupplierPreference,
}) => {
  const { t } = useTranslation('client');
  const cartItems = useCart(s => s.items);

  const productCartItems = useMemo(() => {
    return cartItems.filter(t => t.productId === productId);
  }, [cartItems, productId]);

  // Rozhoduje den závozu dodavatele, ne dnešek — cenu, která začne platit až za pár dní, si
  // zákazník musí umět koupit, když na ni v den dodání dosáhne. Stejné pravidlo drží BE
  // (`PriceVariantValidity`), takže co je tady klikatelné, projde i do košíku.
  const isDisabled = (supplier: Supplier) => !isVariantValidOn(supplier.priceVariant, supplier.deliveryDate);

  return (
    <div className="w-full pt-[17px] border-t-[1.50px] border-gray-100 flex flex-col justify-start items-start gap-3">
      {/* Hlavička sekce dodavatelů */}

      <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2>{t('productDetail.availableSuppliers')}</h2>
        {onAddSupplier && (
          <a className="inline-flex items-center gap-1 text-controls-link-light-primary-default cursor-pointer" onClick={onAddSupplier}>
            <span className="text-lg leading-none">+</span>
            <span className="basic-body-m-bold">{t('productDetail.addSupplier')}</span>
          </a>
        )}
      </div>
      {/* Seznam dodavatelů */}
      <div className="w-full flex flex-col gap-2">
        {suppliers.map(supplier => {
          const cartItem = productCartItems.find(t => t.priceVariantId === supplier.priceVariant.id);
          // Označení dodavatele odlišíme i na řádku, ne jen ikonou — nabízí se vždy ten první.
          const preferenceStyle =
            supplier.supplierPreference === SupplierPreference.Preferred
              ? 'border-controls-link-light-primary-default'
              : supplier.supplierPreference === SupplierPreference.NonPreferred
                ? 'border-[#da1e28] opacity-60'
                : 'border-gray-300';
          return (
            <div key={supplier.priceVariant.id} className={`border ${preferenceStyle} rounded-md p-4 mb-4  hover:bg-gray-50`}>
              <label className={'flex flex-col sm:flex-row gap-2.5 w-full cursor-pointer sm:items-center'}>
                <div className={'flex flex-row gap-2.5 items-center flex-1 min-w-0'}>
                  <div className={'flex flex-col mt-1 shrink-0'} onClick={() => onSelectPriceVariant(supplier.priceVariant)}>
                    <input type="radio" name="radio" onClick={() => {}} checked={selectedPriceVariant.id == supplier.priceVariant.id} disabled={isDisabled(supplier)} />
                  </div>
                  <div className={'flex flex-col flex-1 min-w-0'}>
                    <div className={'flex flex-row gap-2 items-center flex-wrap'}>
                      <div className={'basic-body-m-bold truncate'}>
                        {supplier.supplierId} - {supplier.supplierName}
                      </div>
                      <PriceTypeComponent priceType={supplier.priceVariant?.priceType} isGaranted={supplier.priceVariant.isGaranted} />
                      <SupplierAvailabilityTag availability={supplier.availability} />
                      {supplier.priceVariant.isManual && (
                        <Tag text={t('productDetail.addSupplier')} color="primary" colorSchema="light" componentState="default" size="xs" />
                      )}
                      {onSetSupplierPreference && (
                        <SupplierPreferenceToggle
                          preference={supplier.supplierPreference}
                          onChange={preference => onSetSupplierPreference(supplier.supplierId, preference)}
                        />
                      )}
                    </div>
                    <div className={'basic-body-s text-light truncate'}>
                      {supplier.priceVariant.id} - {formatPriceGuarantee(supplier.priceVariant.validFrom, supplier.priceVariant.validTo)}
                    </div>
                  </div>
                </div>
                <div className={'flex flex-row sm:flex-row gap-2.5 items-center sm:ml-auto pl-6 sm:pl-0'}>
                  <div className={'shrink-0 w-[140px]'}>
                    {isDisabled(supplier) ? (
                      cartItem ? (
                        <button
                          className="h-8 px-3 bg-white rounded-full outline outline-2 outline-[#da1e28] text-[#da1e28] text-sm font-semibold"
                          onClick={() => onDeleteCartItem(cartItem.id)}
                        >
                          {t('basket.removeFromCart')}
                        </button>
                      ) : (
                        <span className="text-sm text-light">{t('productDetail.productUnavailable')}</span>
                      )
                    ) : (
                      <Counter
                        quantity={cartItem?.quantity}
                        onQuantityChange={v => onAddToCart(supplier.supplierId, supplier.priceVariant, v)}
                        size={'small'}
                        onRemove={() => cartItem && onDeleteCartItem(cartItem.id)}
                        minimumQuantity={supplier.minimumQuantity}
                        onlyMultiply={supplier.onlyMultiply}
                        isAbsolute={false}
                        disabled={false}
                      />
                    )}
                  </div>
                  <div className={'flex flex-col items-end shrink-0'}>
                    <div className={'basic-body-m-bold whitespace-nowrap'}>
                      {formatMoney(supplier.priceVariant.currentPrice)} / {formatUnit(supplier.priceVariant.priceUnit)}
                    </div>
                    <div className={'basic-body-xs text-light whitespace-nowrap'}>
                      {formatMoney(supplier.priceVariant.auctionPrice)}/{supplier.auctionAmount} {formatUnit(supplier.auctionUnit as any)}
                    </div>
                  </div>
                </div>
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SuppliersList;
